import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { AUTH_RETRIED } from '../../http-context.tokens';
import { UserControllerService } from '../services/user-controller.service';

/**
 * Shared in-flight refresh so concurrent 401s wait on one POST /users/refresh.
 *
 * Load-bearing, not an optimisation: the refresh token is single-use and
 * rotated server-side the moment it is presented, so two racing refreshes would
 * have one succeed and the other present an already-consumed credential.
 */
let sessionRefresh$: Observable<string> | null = null;

function refreshSession(userController: UserControllerService): Observable<string> {
  if (!sessionRefresh$) {
    sessionRefresh$ = userController.silentRelogin().pipe(
      map((login) => login.sessionId),
      finalize(() => {
        // Defer clear so concurrent 401 handlers in the same turn still share
        // this in-flight login (sync `of()` completes before the next subscribe).
        queueMicrotask(() => {
          sessionRefresh$ = null;
        });
      }),
      // refCount stays false on purpose. With refCount:true the shared
      // subscription is torn down when the last subscriber leaves, so a
      // cancelled request — component destroyed, route change — would abort the
      // refresh mid-flight. Harmless when recovery re-logged in from cached
      // identity, because that was repeatable; not harmless now: if the aborted
      // call still reached the server the token is spent, and we would hold a
      // dead credential with no way to tell. Let it run to completion so the
      // rotated token always gets stored.
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }
  return sessionRefresh$;
}

/** @internal test helper — resets the shared refresh mutex between specs. */
export function resetAuthSessionRefreshForTests(): void {
  sessionRefresh$ = null;
}

/**
 * On HTTP 401 (expired x-auth-session), silently re-login via cached Telegram /
 * URL credentials, then retry the original request once with the new session.
 * If re-login fails, navigate to /401.
 */
export const authSessionInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.includes('v1/users/login')) {
    return next(req);
  }

  const userController = inject(UserControllerService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || req.context.get(AUTH_RETRIED)) {
        return throwError(() => error);
      }

      return refreshSession(userController).pipe(
        switchMap((sessionId) => {
          const retryReq = req.clone({
            setHeaders: { 'x-auth-session': sessionId },
            context: req.context.set(AUTH_RETRIED, true),
          });
          return next(retryReq);
        }),
        catchError((refreshError) => {
          void router.navigate(['/401']);
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
